import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', text: '', rating: 5 });
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/admin/testimonials`, { headers });
      setTestimonials(res.data.testimonials || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) return;
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/testimonials`, form, { headers });
      setForm({ name: '', role: '', text: '', rating: 5 });
      setShowForm(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear testimonio');
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibility = async (id, currentVisible) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/testimonials/${id}`, { is_visible: !currentVisible }, { headers });
      load();
    } catch (err) {
      alert('Error al actualizar');
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Eliminar este testimonio?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/testimonials/${id}`, { headers });
      load();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Header />
        <div className="py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white" data-testid="admin-testimonials-title">
                Gestionar Testimonios
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Administra los testimonios que se muestran en la pagina principal</p>
            </div>
            <button onClick={() => setShowForm(!showForm)} data-testid="add-testimonial-btn"
              className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors">
              {showForm ? 'Cancelar' : '+ Nuevo Testimonio'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} className="bg-white dark:bg-[#1a2530] rounded-2xl p-6 shadow-lg mb-8" data-testid="testimonial-form">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre *</label>
                  <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#283039] text-gray-900 dark:text-white"
                    placeholder="Maria Garcia" required data-testid="testimonial-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rol / Cargo</label>
                  <input type="text" value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#283039] text-gray-900 dark:text-white"
                    placeholder="Emprendedora Digital" data-testid="testimonial-role-input" />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Testimonio *</label>
                <textarea value={form.text} onChange={e => setForm({...form, text: e.target.value})} rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#283039] text-gray-900 dark:text-white"
                  placeholder="GPT-JDP me ha ayudado a..." required data-testid="testimonial-text-input" />
              </div>
              <div className="flex items-center gap-4 mb-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Rating:</label>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button" onClick={() => setForm({...form, rating: n})}
                      className={`w-8 h-8 rounded-full text-sm font-bold ${form.rating >= n ? 'bg-yellow-400 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'}`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <button type="submit" disabled={saving} data-testid="submit-testimonial-btn"
                className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50">
                {saving ? 'Guardando...' : 'Guardar Testimonio'}
              </button>
            </form>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-16 bg-white dark:bg-[#1a2530] rounded-2xl">
              <p className="text-4xl mb-3">💬</p>
              <p className="text-gray-500 dark:text-gray-400">No hay testimonios aun. Crea el primero.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {testimonials.map(t => (
                <div key={t.id} className={`bg-white dark:bg-[#1a2530] rounded-2xl p-5 shadow-lg border-l-4 ${t.is_visible ? 'border-green-500' : 'border-gray-300 dark:border-gray-600 opacity-60'}`}
                  data-testid={`admin-testimonial-${t.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-bold text-xs">
                        {t.avatar_initials || t.name?.slice(0,2).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white text-sm">{t.name}</p>
                        <p className="text-xs text-gray-500">{t.role || 'Sin cargo'}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => toggleVisibility(t.id, t.is_visible)} data-testid={`toggle-visibility-${t.id}`}
                        className={`px-2 py-1 rounded text-xs font-medium ${t.is_visible ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
                        {t.is_visible ? 'Visible' : 'Oculto'}
                      </button>
                      <button onClick={() => deleteTestimonial(t.id)} data-testid={`delete-testimonial-${t.id}`}
                        className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200">
                        Eliminar
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">"{t.text}"</p>
                  <div className="flex gap-0.5 mt-2">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className={`w-3.5 h-3.5 ${i < t.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default AdminTestimonials;
